/* Moderation: what administrators see of the reports about public collections, and the three
   things they can do about them. Each is a function of the database that asks whether the
   caller is an administrator before anything else: what is here decides nothing, it asks.
   docs/specs/2026-09-21-moderation-design.md */

import { z } from "@preflop/core";
import type { SupabaseClient } from "@supabase/supabase-js";
import { DataError } from "./collections";

export const MODERATION_NOTE_MAX = 500;

const queueRowSchema = z.object({
  collection_id: z.uuid(),
  name: z.string(),
  description: z.string(),
  owner_name: z.string(),
  visibility: z.enum(["private", "public"]),
  spot_count: z.number().int(),
  moderated_at: z.string().nullable(),
  moderation_note: z.string(),
  waiting: z.number().int(),
  reports: z.number().int(),
  reasons: z.array(z.string()),
  notes: z.array(z.string()),
  latest_at: z.string().nullable(),
});

/** A collection that has reports waiting, or that was taken down. Never who reported it. */
export interface ModerationCase {
  collectionId: string;
  name: string;
  description: string;
  ownerName: string;
  visibility: "private" | "public";
  spotCount: number;
  /** When it was taken down; null when it was not. */
  moderatedAt: string | null;
  moderationNote: string;
  /** Reports nobody has dealt with yet. */
  waiting: number;
  /** Reports in all, handled or not. */
  reports: number;
  /** The reasons of the waiting reports, each once. */
  reasons: string[];
  /** The notes of the waiting reports, the newest first. */
  notes: string[];
  latestAt: string | null;
}

export const moderationCaseFromRow = (raw: unknown): ModerationCase => {
  const r = queueRowSchema.parse(raw);
  return {
    collectionId: r.collection_id,
    name: r.name,
    description: r.description,
    ownerName: r.owner_name,
    visibility: r.visibility,
    spotCount: r.spot_count,
    moderatedAt: r.moderated_at,
    moderationNote: r.moderation_note,
    waiting: r.waiting,
    reports: r.reports,
    reasons: r.reasons,
    notes: r.notes,
    latestAt: r.latest_at,
  };
};

export interface ModerationApi {
  /** What is waiting, the most reported first, then what was taken down. Administrators only:
      anyone else is refused by the database. */
  queue(): Promise<ModerationCase[]>;
  /** Private at once, and not to be published again until released. The owner reads the note. */
  takeDown(collectionId: string, note: string): Promise<void>;
  /** Its owner may publish it again. */
  release(collectionId: string): Promise<void>;
  /** The collection is fine: its waiting reports are kept, and marked as dealt with. */
  dismiss(collectionId: string): Promise<void>;
}

export function createModerationApi(db: SupabaseClient): ModerationApi {
  const call = async (fn: string, args: Record<string, unknown>): Promise<void> => {
    const { error } = await db.rpc(fn, args);
    if (error) throw new DataError(error.message, error.code);
  };
  return {
    async queue() {
      const { data, error } = await db.rpc("moderation_queue");
      if (error) throw new DataError(error.message, error.code);
      return (data as unknown[]).map(moderationCaseFromRow);
    },
    takeDown: (collectionId, note) =>
      call("moderate_collection", {
        target: collectionId,
        note: note.trim().slice(0, MODERATION_NOTE_MAX),
      }),
    release: (collectionId) => call("release_collection", { target: collectionId }),
    dismiss: (collectionId) => call("dismiss_reports", { target: collectionId }),
  };
}
